import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PersonIcon from '@mui/icons-material/Person';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAgenciaStore } from "../zustand/useAgenciaStore";
import { useAuthStore } from "../zustand/useAuthStore";
import { useThemeStore } from "../zustand/useThemeStore";
import { useTheme } from "@mui/material/styles";

const MODULE_TITLES: Record<string, string> = {
  "/": "Paquetes",
  "/users": "Usuarios",
  "/analytics": "Estadísticas",
  "/promotions": "Promociones",
  "/shipments": "Envíos",
  "/referidos": "Referidos/Puntos",
  "/settings": "Configuración",
  "/profile": "Configuración",
};

export default function NavBar() {
  const { agencia } = useAgenciaStore();
  const { user } = useAuthStore();
  const { mode, setMode } = useThemeStore();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const currentModuleTitle = MODULE_TITLES[location.pathname] || "";
  const settings = [
    {
      title: "Cambiar contraseña",
      onClick: () => {
        navigate("/reset-password");
      },
    },
    {
      title: "Cerrar sesión",
      onClick: () => {
        localStorage.removeItem("auth-storage");
        window.location.reload();
      },
    },
  ];

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElTheme, setAnchorElTheme] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenThemeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTheme(event.currentTarget);
  };

  const handleCloseThemeMenu = () => {
    setAnchorElTheme(null);
  };

  const handleThemeChange = (newMode: "light" | "dark" | "system") => {
    setMode(newMode);
    handleCloseThemeMenu();
  };

  const getThemeIcon = () => {
    switch (mode) {
      case "dark":
        return <DarkModeIcon />;
      case "light":
        return <LightModeIcon />;
      case "system":
        return <SettingsBrightnessIcon />;
      default:
        return <SettingsBrightnessIcon />;
    }
  };

  const getThemeTooltip = () => {
    switch (mode) {
      case "dark":
        return "Modo oscuro";
      case "light":
        return "Modo claro";
      case "system":
        return "Seguir sistema";
      default:
        return "Tema";
    }
  };

  const capitalize = (word: string) => {
    return word.charAt(0).toUpperCase() + word.slice(1)
  }

  return (
    <AppBar
      position="static"
      sx={{ 
        backgroundColor: theme.palette.custom?.appBar || "#353a40", 
        borderRadius: 2 
      }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: "bold",
              color: theme.palette.custom?.drawerText || "#FFFFFF",
            }}
          >
            {currentModuleTitle}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box 
              sx={{ 
                display: "flex", 
                flexDirection: "row", 
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                marginRight: 0.5,
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: "0.9375rem", 
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: theme.palette.custom?.drawerText || "#FFFFFF",
                }}
              >
                {user?.name && user?.lastName
                  ? `${capitalize(user.name)} ${capitalize(user.lastName)}`
                  : capitalize(`${agencia?.nombre}`)}
              </Typography>
              {user?.role && (
                <Chip
                  label={user.role}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    backgroundColor: theme.palette.mode === "dark" 
                      ? "rgba(255, 255, 255, 0.15)" 
                      : "rgba(255, 255, 255, 0.25)",
                    color: theme.palette.custom?.drawerText || "#FFFFFF",
                    "& .MuiChip-label": {
                      padding: "0 8px",
                    },
                  }}
                />
              )}
            </Box>
            <Tooltip title={getThemeTooltip()}>
              <IconButton onClick={handleOpenThemeMenu} sx={{ color: "inherit" }}>
                {getThemeIcon()}
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              anchorEl={anchorElTheme}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElTheme)}
              onClose={handleCloseThemeMenu}
            >
              <MenuItem
                onClick={() => handleThemeChange("light")}
                selected={mode === "light"}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LightModeIcon fontSize="small" />
                  <Typography>Modo claro</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => handleThemeChange("dark")}
                selected={mode === "dark"}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DarkModeIcon fontSize="small" />
                  <Typography>Modo oscuro</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => handleThemeChange("system")}
                selected={mode === "system"}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SettingsBrightnessIcon fontSize="small" />
                  <Typography>Seguir sistema</Typography>
                </Box>
              </MenuItem>
            </Menu>
            <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Cerrar sesión">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar>
                  <PersonIcon />
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id={`menu-appbar-${Math.random().toString(36).substring(2, 15)}`}
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem
                  key={setting.title}
                  onClick={() => {
                    handleCloseUserMenu();
                    setting.onClick();
                  }}
                >
                  <Typography sx={{ textAlign: "center" }}>
                    {setting.title}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
